import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Pencil, Trash2, Award, ExternalLink, Lock, Crown, Upload, Image } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sponsorTiers = [
  { value: 'gold', label: 'Gold', color: 'bg-yellow-500' },
  { value: 'silver', label: 'Silver', color: 'bg-gray-400' },
  { value: 'bronze', label: 'Bronze', color: 'bg-orange-600' }
];

export default function SponsorsPage() {
  const { token, organization } = useAuth();
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website_url: '',
    tier: 'gold'
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [logoMode, setLogoMode] = useState('upload'); // 'upload' or 'url'
  const fileInputRef = useRef(null);

  const canManageSponsors = organization?.plan === 'pro' || organization?.plan === 'enterprise';
  const maxSponsors = organization?.plan === 'enterprise' ? -1 : (organization?.plan === 'pro' ? 5 : 0);

  useEffect(() => {
    if (organization) {
      fetchSponsors();
    }
  }, [organization]);

  const fetchSponsors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sponsors/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSponsors(data);
      }
    } catch (error) {
      console.error('Failed to fetch sponsors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoBase64(reader.result);
      setLogoPreview(reader.result);
      setFormData(prev => ({ ...prev, logo_url: '' }));
    };
    reader.readAsDataURL(file);
  };

  const clearLogoUpload = () => {
    setLogoBase64(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const logoValue = logoBase64 || formData.logo_url;
    if (!logoValue) {
      toast.error('Please upload a logo or provide a logo URL');
      return;
    }
    
    try {
      const payload = {
        name: formData.name,
        website_url: formData.website_url,
        tier: formData.tier,
        logo_url: logoBase64 ? '' : formData.logo_url,
        logo_data: logoBase64 || null
      };

      if (editingSponsor) {
        const response = await fetch(`${API_URL}/api/sponsors/${editingSponsor.sponsor_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Failed to update sponsor');
        toast.success('Sponsor updated!');
      } else {
        const response = await fetch(`${API_URL}/api/sponsors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...payload,
            org_id: organization.org_id
          })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to create sponsor');
        }
        toast.success('Sponsor added!');
      }
      
      setDialogOpen(false);
      setEditingSponsor(null);
      setFormData({ name: '', logo_url: '', website_url: '', tier: 'gold' });
      clearLogoUpload();
      fetchSponsors();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (sponsorId) => {
    if (!confirm('Are you sure you want to remove this sponsor?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/sponsors/${sponsorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete sponsor');
      toast.success('Sponsor removed!');
      fetchSponsors();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditDialog = (sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({
      name: sponsor.name,
      logo_url: sponsor.logo_url || '',
      website_url: sponsor.website_url || '',
      tier: sponsor.tier
    });
    if (sponsor.logo_data) {
      setLogoPreview(sponsor.logo_data);
      setLogoBase64(sponsor.logo_data);
      setLogoMode('upload');
    } else if (sponsor.logo_url) {
      setLogoPreview(sponsor.logo_url);
      setLogoBase64(null);
      setLogoMode('url');
    } else {
      clearLogoUpload();
      setLogoMode('upload');
    }
    setDialogOpen(true);
  };

  const getTierColor = (tier) => {
    const t = sponsorTiers.find(s => s.value === tier);
    return t?.color || 'bg-gray-500';
  };

  // Group sponsors by tier
  const sponsorsByTier = {
    gold: sponsors.filter(s => s.tier === 'gold'),
    silver: sponsors.filter(s => s.tier === 'silver'),
    bronze: sponsors.filter(s => s.tier === 'bronze')
  };

  return (
    <DashboardLayout>
      <div data-testid="sponsors-page" className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
              Sponsors
            </h1>
            <p className="text-gray-600 mt-2">
              {canManageSponsors 
                ? `Manage tournament sponsors (${sponsors.length}${maxSponsors !== -1 ? `/${maxSponsors}` : ''})`
                : 'Showcase your tournament sponsors'
              }
            </p>
          </div>
          {canManageSponsors ? (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-black btn-lift"
                  data-testid="add-sponsor-btn"
                  onClick={() => {
                    setEditingSponsor(null);
                    setFormData({ name: '', logo_url: '', website_url: '', tier: 'gold' });
                    clearLogoUpload();
                    setLogoMode('upload');
                  }}
                >
                  <Plus size={18} className="mr-2" />
                  Add Sponsor
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 text-black">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">
                    {editingSponsor ? 'Edit Sponsor' : 'Add New Sponsor'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-gray-700">Sponsor Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Nike, Adidas"
                      required
                      data-testid="sponsor-name-input"
                      className="mt-2 bg-gray-100 border-gray-200 text-black"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Logo</Label>
                    <div className="mt-2 flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => { setLogoMode('upload'); setFormData(prev => ({ ...prev, logo_url: '' })); }}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${logoMode === 'upload' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}
                      >
                        <Upload size={12} className="inline mr-1" /> Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => { setLogoMode('url'); clearLogoUpload(); }}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${logoMode === 'url' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}
                      >
                        <Image size={12} className="inline mr-1" /> URL
                      </button>
                    </div>
                    {logoMode === 'upload' ? (
                      <div>
                        {logoPreview ? (
                          <div className="relative inline-block">
                            <img src={logoPreview} alt="Logo preview" className="h-20 w-20 object-contain rounded-lg border border-gray-200 bg-gray-100" />
                            <button
                              type="button"
                              onClick={clearLogoUpload}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-black text-xs"
                            >
                              &times;
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-emerald-500/40 transition-colors"
                          >
                            <Upload size={24} className="mx-auto text-gray-600 mb-2" />
                            <p className="text-sm text-gray-600">Click to upload logo</p>
                            <p className="text-xs text-gray-500 mt-1">JPG, PNG, WebP (max 2MB)</p>
                          </button>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileSelect}
                          className="hidden"
                          data-testid="sponsor-logo-file-input"
                        />
                      </div>
                    ) : (
                      <Input
                        value={formData.logo_url}
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                        placeholder="https://example.com/logo.png"
                        data-testid="sponsor-logo-input"
                        className="bg-gray-100 border-gray-200 text-black"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-700">Website URL (optional)</Label>
                    <Input
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      placeholder="https://sponsor-website.com"
                      data-testid="sponsor-website-input"
                      className="mt-2 bg-gray-100 border-gray-200 text-black"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Sponsorship Tier</Label>
                    <Select 
                      value={formData.tier} 
                      onValueChange={(value) => setFormData({ ...formData, tier: value })}
                    >
                      <SelectTrigger className="mt-2 bg-gray-100 border-gray-200 text-black">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {sponsorTiers.map((tier) => (
                          <SelectItem key={tier.value} value={tier.value} className="text-black hover:bg-gray-100">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${tier.color}`} />
                              {tier.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 border-gray-200 text-black hover:bg-gray-100"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black"
                      data-testid="sponsor-submit-btn"
                    >
                      {editingSponsor ? 'Update' : 'Add Sponsor'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Button 
              onClick={() => navigate('/billing')}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black btn-lift"
              data-testid="upgrade-for-sponsors-btn"
            >
              <Crown size={18} className="mr-2" />
              Upgrade to Pro
            </Button>
          )}
        </div>

        {/* Upgrade Banner for non-Pro users */}
        {!canManageSponsors && (
          <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Lock className="text-yellow-500" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-black font-semibold mb-1">Unlock Sponsor Management</h3>
                  <p className="text-gray-600 text-sm">
                    Upgrade to <span className="text-yellow-500 font-medium">Pro</span> to add up to 5 sponsors, 
                    or <span className="text-orange-500 font-medium">Enterprise</span> for unlimited sponsors. 
                    Showcase your sponsors on your public tournament page!
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/billing')}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                >
                  View Plans
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sponsors Display */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : sponsors.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="p-12 text-center">
              <Award className="mx-auto text-gray-600 mb-4" size={48} />
              <h3 className="text-black text-lg font-medium mb-2">No sponsors yet</h3>
              <p className="text-gray-500">
                {canManageSponsors 
                  ? 'Add sponsors to showcase them on your tournament page'
                  : 'Upgrade to Pro to add sponsors'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Gold Sponsors */}
            {sponsorsByTier.gold.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-yellow-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full" />
                  Gold Sponsors
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sponsorsByTier.gold.map((sponsor) => (
                    <SponsorCard 
                      key={sponsor.sponsor_id} 
                      sponsor={sponsor} 
                      onEdit={openEditDialog}
                      onDelete={handleDelete}
                      canEdit={canManageSponsors}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Silver Sponsors */}
            {sponsorsByTier.silver.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-gray-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-400 rounded-full" />
                  Silver Sponsors
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {sponsorsByTier.silver.map((sponsor) => (
                    <SponsorCard 
                      key={sponsor.sponsor_id} 
                      sponsor={sponsor} 
                      onEdit={openEditDialog}
                      onDelete={handleDelete}
                      canEdit={canManageSponsors}
                      compact
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bronze Sponsors */}
            {sponsorsByTier.bronze.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-orange-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-600 rounded-full" />
                  Bronze Sponsors
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {sponsorsByTier.bronze.map((sponsor) => (
                    <SponsorCard 
                      key={sponsor.sponsor_id} 
                      sponsor={sponsor} 
                      onEdit={openEditDialog}
                      onDelete={handleDelete}
                      canEdit={canManageSponsors}
                      compact
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function SponsorCard({ sponsor, onEdit, onDelete, canEdit, compact = false }) {
  return (
    <Card 
      className="bg-white border-gray-200 card-hover overflow-hidden group"
      data-testid={`sponsor-card-${sponsor.sponsor_id}`}
    >
      <CardContent className={compact ? "p-4" : "p-6"}>
        <div className={`flex ${compact ? 'flex-col items-center text-center' : 'items-center gap-4'}`}>
          <div className={`${compact ? 'w-16 h-16 mb-3' : 'w-20 h-20'} bg-gray-100 rounded-lg flex items-center justify-center p-2`}>
            <img 
              src={sponsor.logo_data || sponsor.logo_url} 
              alt={sponsor.name}
              className="max-w-full max-h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className={compact ? '' : 'flex-1'}>
            <h3 className={`font-medium text-black ${compact ? 'text-sm' : 'text-lg'}`}>
              {sponsor.name}
            </h3>
            {sponsor.website_url && !compact && (
              <a 
                href={sponsor.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-500 text-sm hover:text-emerald-400 flex items-center gap-1 mt-1"
              >
                Visit website <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
        
        {canEdit && (
          <div className={`flex gap-2 ${compact ? 'mt-3' : 'mt-4'} opacity-0 group-hover:opacity-100 transition-opacity`}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(sponsor)}
              className="flex-1 border-gray-200 text-black hover:bg-gray-100"
            >
              <Pencil size={14} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(sponsor.sponsor_id)}
              className="border-red-500/20 text-red-500 hover:bg-red-500/10"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
